import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { useCallback, useEffect, useMemo } from 'react'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import { useHistory, useLocation } from 'react-router'

import { useModalContext } from '@/components/Modal/contexts/ModalContext'
import { useAttendanceContext } from '@/modules/Attendance/contexts/AttendanceContex'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import {
  ATTENDANCE_STATES,
  SCHEDULING_STATES,
} from '@/modules/RealtimeAttendance/realtime-attendance-constants'
import { notify } from '@/components/BrowserNotification'
import { capitalize, returnFormattedDate } from '@/utils/fnUtils'
import { setCache } from '@/utils/cache'

import { verifyTokenStatus } from '@/modules/RealtimeAttendance/realtime-attendance.services'
import { toast } from '@conexasaude/hero'
import {
  IAttendanceConfig,
  IHandleAttendanceStartWithToken,
} from './realtime-attendance-types'
import { useMedicalSpecialitiesContext } from '../MedicalSpecialities/contexts/MedicalSpecialitiesContext'

const useRealtimeAttendance = (): void => {
  const {
    realtimeAttendance: { immediate, nextScheduled, call },
    startTimer,
    clearTimer,
  } = useRealtimeAttendanceContext()
  const { push } = useHistory()
  const { pathname } = useLocation()
  const { showModal } = useModalContext()
  const { clinic } = useAuthContext()
  const { resetAttendance } = useAttendanceContext()
  const { withoutInteractionAttendance, embed } = useThemeGeneratorContext()
  const { specialities } = useMedicalSpecialitiesContext()

  const immediateSpeciality = useMemo(() => {
    return specialities.find(s => s.id === immediate?.council)
  }, [immediate, specialities])

  const scheduledSpeciality = useMemo(() => {
    return specialities.find(s => s.id === nextScheduled?.council)
  }, [nextScheduled, specialities])

  const canRedirect = useCallback(() => {
    if (pathname === '/trocar-senha') return false
    return true
  }, [pathname])

  const handleAttendanceStartWithToken = async (
    params: IHandleAttendanceStartWithToken,
  ): Promise<void> => {
    const { attendanceId, modalToRender, modalTitle } = params

    try {
      const tokenIsValidated = await verifyTokenStatus(Number(attendanceId))
      if (tokenIsValidated) {
        showModal(modalToRender, {
          title: modalTitle,
          lockable: true,
          persistent: true,
        })
      } else {
        showModal('CALL_STARTED_WITH_TOKEN', {
          title: 'Informe o token de segurança Amil',
          persistent: true,
        })
      }
    } catch (err) {
      push('/')
      toast(err?.response?.data?.msg, {
        variant: 'danger',
        autoClose: false,
        position: 'top-right',
      })
    }
  }

  useEffect(() => {
    if (!canRedirect()) return
    switch (immediate?.status) {
      case ATTENDANCE_STATES.WAITING_ATTENDANCE:
        push('/atendimento-imediato/waiting')
        break
      case ATTENDANCE_STATES.ATTENDANCE_STARTED:
        push('/atendimento-imediato/waiting')
        notify(
          'Só um minuto',
          `${capitalize(
            immediate.professionalName.trim().toLowerCase(),
          )} já está analisando suas informações e em breve iniciará o atendimento.`,
          immediate.professionalPhoto,
        )
        break
      case ATTENDANCE_STATES.ATTENDANCE_END:
        setCache('hiddenGuide', '')
        resetAttendance()

        clinic?.flag?.DesabilitarNpsTelemedicina
          ? push('/')
          : push(`/avaliacao/${immediate?.attendanceId}`)

        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_MEDIC:
        setCache('hiddenGuide', '')
        resetAttendance()
        clearTimer()
        showModal('CANCELLED_BY_MEDIC', {
          title: 'Consulta cancelada',
          lockable: true,
          persistent: true,
        })
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_TIME:
        setCache('hiddenGuide', '')
        resetAttendance()
        clearTimer()

        if (immediateSpeciality?.canSchedule && !embed?.full) {
          showModal('CANCELLED_BY_EXCEEDED_TIME_WITH_SCHEDULE', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
          })
        } else {
          showModal('CANCELLED_BY_EXCEEDED_TIME', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
          })
        }
        break
      case ATTENDANCE_STATES.CALL_STARTED:
        if (pathname === '/chamada') return

        if (withoutInteractionAttendance?.value) {
          push('/chamada')
          return
        }

        notify(
          'Tudo pronto para sua consulta',
          `${capitalize(
            immediate.professionalName.trim().toLowerCase(),
          )} já está aguardando por você. Vamos começar?`,
          immediate.professionalPhoto,
        )

        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId: call?.attendanceId,
            modalToRender: 'CALL_STARTED',
            modalTitle: 'Tudo pronto para sua consulta',
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          showModal('CALL_STARTED', {
            title: 'Tudo pronto para sua consulta',
            lockable: true,
            persistent: true,
          })
        }

        startTimer()
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_PATIENT_BLOCKED:
        setCache('hiddenGuide', '')
        resetAttendance()
        showModal('CANCELLED_BY_CLINIC', {
          title:
            'Seu acesso está bloqueado para realizar consultas. Você precisa sair da sala de espera.',
          lockable: true,
          persistent: true,
        })
        clearTimer()
        break
      case ATTENDANCE_STATES.CALL_RESTARTED:
        if (pathname === '/chamada') return

        notify(
          'Consulta Reiniciada',
          `${capitalize(
            immediate.professionalName.trim().toLowerCase(),
          )} está precisando entrar em contato com você.`,
          immediate.professionalPhoto,
        )

        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId: call?.attendanceId,
            modalToRender: 'CALL_RESTARTED',
            modalTitle: 'Consulta Reiniciada',
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          showModal('CALL_RESTARTED', {
            title: 'Consulta Reiniciada',
            persistent: true,
          })
        }

        startTimer()
        break
      case ATTENDANCE_STATES.CALL_ENDED:
        setCache('hiddenGuide', '')
        resetAttendance()

        if (call) {
          if (withoutInteractionAttendance?.value) {
            clinic?.linkRedirectAfterAttendance
              ? (window.location.href = clinic?.linkRedirectAfterAttendance)
              : push('/')
            return
          }

          if (clinic?.flag?.DesabilitarNpsTelemedicina) {
            push('/')
            return
          }

          push(`/avaliacao/${call?.attendanceId}`)
        } else {
          showModal('CANCELLED_BY_MEDIC', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
          })
        }
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate?.status])

  useEffect(() => {
    if (!canRedirect()) return
    switch (nextScheduled?.status) {
      case ATTENDANCE_STATES.ATTENDANCE_STARTED:
        if (pathname !== `/atendimento-agendado/${nextScheduled.id}`) {
          setTimeout(() => {
            notify('Conexa Saúde', `Sua consulta esta quase começando.`)
            showModal('ATTENDANCE_STARTED', {
              title: 'Falta pouco!',
            })
          }, 2000)

          setTimeout(() => {
            push(`/atendimento-agendado/${nextScheduled.id}`)
          }, 10000)
        }
        break
      case ATTENDANCE_STATES.ATTENDANCE_END:
        setCache('hiddenGuide', '')
        resetAttendance()

        clinic?.flag?.DesabilitarNpsTelemedicina
          ? push('/')
          : push(`/avaliacao/${nextScheduled.id}`)
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_MEDIC:
        setCache('hiddenGuide', '')
        resetAttendance()
        clearTimer()
        showModal('CANCELLED_BY_MEDIC', {
          title: 'Consulta cancelada',
          lockable: true,
          persistent: true,
        })
        break
      case ATTENDANCE_STATES.ATTENDANCE_CANCELLED_TIME:
        setCache('hiddenGuide', '')
        resetAttendance()
        clearTimer()

        if (scheduledSpeciality?.canSchedule && !embed?.full) {
          showModal('CANCELLED_BY_EXCEEDED_TIME_WITH_SCHEDULE', {
            title: 'Chamada cancelada',
            lockable: true,
            persistent: true,
          })
        } else {
          showModal('CANCELLED_BY_EXCEEDED_TIME', {
            title: 'Chamada cancelada',
            lockable: true,
            persistent: true,
          })
        }

        break
      case ATTENDANCE_STATES.CALL_STARTED:
        if (pathname === '/chamada') return
        notify(
          'Tudo pronto para sua consulta',
          `${capitalize(
            nextScheduled.professionalName?.trim().toLowerCase(),
          )} já está aguardando por você. Vamos começar?`,
          nextScheduled.professionalPhoto,
        )

        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId: call?.attendanceId,
            modalToRender: 'SCHEDULED_CALL_STARTED',
            modalTitle: 'Tudo pronto para sua consulta',
            isScheduled: true,
            professionalId: call?.professionalId,
            scheduleDateAndHour: returnFormattedDate(),
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          showModal('SCHEDULED_CALL_STARTED', {
            title: 'Tudo pronto para sua consulta',
            lockable: true,
            persistent: true,
          })
        }

        startTimer()
        break
      case ATTENDANCE_STATES.CALL_RESTARTED:
        if (pathname === '/chamada') return

        notify(
          'Consulta Reiniciada',
          `${capitalize(
            nextScheduled.professionalName?.trim().toLowerCase(),
          )} está precisando entrar em contato com você.`,
          nextScheduled.professionalPhoto,
        )
        if (clinic?.flag?.LiberarChamadaPorTokenAutorizacaoExterno) {
          const attendanceConfig: IAttendanceConfig = {
            attendanceId: call?.attendanceId,
            modalToRender: 'CALL_RESTARTED',
            modalTitle: 'Consulta Reiniciada',
            isScheduled: true,
            professionalId: call?.professionalId,
            scheduleDateAndHour: returnFormattedDate(),
          }
          handleAttendanceStartWithToken(attendanceConfig)
        } else {
          showModal('CALL_RESTARTED', {
            title: 'Consulta Reiniciada',
            persistent: true,
          })
        }
        startTimer()
        break
      case ATTENDANCE_STATES.CALL_ENDED:
        resetAttendance()
        if (call) {
          clinic?.flag?.DesabilitarNpsTelemedicina
            ? push('/')
            : push(`/avaliacao/${call?.attendanceId}`)
        } else {
          showModal('CANCELLED_BY_MEDIC', {
            title: 'Consulta cancelada',
            lockable: true,
            persistent: true,
          })
        }
        break
    }

    if (nextScheduled?.scheduledStatus === SCHEDULING_STATES.ATTENDANCE_LATE) {
      showModal('ATTENDANCE_LATE', {
        title: 'Aguarde',
      })
      notify('Conexa Saúde', `Sua consulta esta quase começando.`)
    }

    if (nextScheduled?.scheduledStatus === SCHEDULING_STATES.RESCHEDULED) {
      showModal('RESCHEDULED_BY_MEDIC', {
        title: 'Consulta reagendada',
        width: '480px',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextScheduled?.status, nextScheduled?.scheduledStatus])
}

export default useRealtimeAttendance
